/**
 * BarometerModal - modal displaying deficit professions for selected districts.
 *
 * Features:
 * - Summary line with "Duży deficyt" and "Deficyt" counts
 * - Two visual groups: large shortage (red) and shortage (orange)
 * - Multi-district: dropdown selector to switch between districts
 * - Optional voivodeship-level data section
 * - Accessibility: role="dialog", aria-modal, Escape to close, focus trap, focus return
 * - Uses neutral-* Tailwind classes (not gray-*)
 *
 * @see docs/specifications/2026-03-06-kfs-barometr-zawodow.md
 */
import React, { useState, useEffect, useRef } from "react";
import { BarometerDistrict, BarometerVoivodeship } from "../../../../types/grants";

interface BarometerModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** District barometer data */
  districts: BarometerDistrict[];
  /** Barometer data year */
  year: number;
  /** Optional voivodeship-level data */
  voivodeship?: BarometerVoivodeship;
  /** Ref to the trigger element for focus return on close */
  triggerRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Renders a group of professions with colored indicators.
 */
const ProfessionGroup: React.FC<{
  professions: string[];
  variant: "large" | "normal";
  headingId?: string;
}> = ({ professions, variant, headingId }) => {
  if (professions.length === 0) return null;

  const bgColor = variant === "large" ? "bg-red-50" : "bg-orange-50";
  const borderColor = variant === "large" ? "border-red-400" : "border-orange-400";

  return (
    <ul className="space-y-1" aria-labelledby={headingId}>
      {professions.map((profession, index) => (
        <li
          key={index}
          className={`${bgColor} border-l-3 ${borderColor} px-2 py-1 text-sm text-left`}
        >
          {profession}
        </li>
      ))}
    </ul>
  );
};

const BarometerModal: React.FC<BarometerModalProps> = ({
  isOpen,
  onClose,
  districts,
  year,
  voivodeship,
  triggerRef,
}) => {
  const [selectedDistrictCode, setSelectedDistrictCode] = useState<string>("");
  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset selected district when districts change or modal opens
  useEffect(() => {
    if (isOpen && districts.length > 0) {
      setSelectedDistrictCode(districts[0].code);
    }
  }, [isOpen, districts]);

  // Focus the dialog when opened
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  // Return focus to trigger on close
  const handleClose = () => {
    onClose();
    if (triggerRef?.current) {
      triggerRef.current.focus();
    }
  };

  if (!isOpen) return null;

  const selectedDistrict = districts.find((d) => d.code === selectedDistrictCode) || districts[0];
  const hasMultipleDistricts = districts.length > 1;
  const hasNoProfessions =
    selectedDistrict &&
    selectedDistrict.largeShortageProfessions.length === 0 &&
    selectedDistrict.shortageProfessions.length === 0;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleClose();
    }
    if (e.key === "Tab" && dialogRef.current) {
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="barometer-modal-title"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg p-4 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto focus:outline-none"
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div>
            <h2 id="barometer-modal-title" className="text-lg font-bold text-neutral-900">
              {"Barometr zawodów - "}{year}
            </h2>
            {selectedDistrict && (
              <div className="text-xs text-neutral-600 mt-1">
                {"Powiat: "}{selectedDistrict.name}
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors ml-4"
            aria-label="Zamknij modal"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* District selector - only when multiple districts */}
        {hasMultipleDistricts && (
          <div className="mb-4">
            <label
              htmlFor="district-select"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              {"Powiat"}
            </label>
            <select
              id="district-select"
              value={selectedDistrictCode}
              onChange={(e) => setSelectedDistrictCode(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none"
            >
              {districts.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* District professions */}
        {selectedDistrict && !hasNoProfessions && (
          <div>
            {/* Summary line - single element containing both "Duży deficyt" and "Deficyt" */}
            <p className="text-sm font-semibold text-neutral-700 mb-3">
              {`Duży deficyt: ${selectedDistrict.largeShortageProfessions.length}, Deficyt: ${selectedDistrict.shortageProfessions.length}`}
            </p>

            {/* Large shortage professions */}
            {selectedDistrict.largeShortageProfessions.length > 0 && (
              <div className="mb-4">
                <div id="district-large-shortage" className="text-xs font-medium text-red-700 mb-1 flex items-center">
                  <span className="inline-block w-2 h-2 bg-red-600 rounded-full mr-2" />
                  <span>{"Zawody z dużym niedoborem ("}{selectedDistrict.largeShortageProfessions.length}{")"}</span>
                </div>
                <ProfessionGroup
                  professions={selectedDistrict.largeShortageProfessions}
                  variant="large"
                  headingId="district-large-shortage"
                />
              </div>
            )}

            {/* Shortage professions */}
            {selectedDistrict.shortageProfessions.length > 0 && (
              <div className="mb-4">
                <div id="district-shortage" className="text-xs font-medium text-orange-700 mb-1 flex items-center">
                  <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-2" />
                  <span>{"Zawody z niedoborem ("}{selectedDistrict.shortageProfessions.length}{")"}</span>
                </div>
                <ProfessionGroup
                  professions={selectedDistrict.shortageProfessions}
                  variant="normal"
                  headingId="district-shortage"
                />
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {hasNoProfessions && (
          <div className="text-left py-4 text-neutral-500">
            <p className="text-sm">
              {"Brak zawodów z deficytem w tym powiecie w "}{year}{" roku."}
            </p>
          </div>
        )}

        {/* Voivodeship section */}
        {voivodeship && (
          <div className="mt-4 pt-4 border-t border-neutral-200">
            <h3 className="text-sm font-semibold text-neutral-700 mb-2">
              {"Dane województwa: "}{voivodeship.name}
            </h3>
            {voivodeship.largeShortageProfessions.length > 0 && (
              <div className="mb-3">
                <div id="voivodeship-large-shortage" className="text-xs font-medium text-red-700 mb-1 flex items-center">
                  <span className="inline-block w-2 h-2 bg-red-600 rounded-full mr-2" />
                  <span>{"Zawody z dużym niedoborem ("}{voivodeship.largeShortageProfessions.length}{")"}</span>
                </div>
                <ProfessionGroup
                  professions={voivodeship.largeShortageProfessions}
                  variant="large"
                  headingId="voivodeship-large-shortage"
                />
              </div>
            )}
            {voivodeship.shortageProfessions.length > 0 && (
              <div className="mb-3">
                <div id="voivodeship-shortage" className="text-xs font-medium text-orange-700 mb-1 flex items-center">
                  <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-2" />
                  <span>{"Zawody z niedoborem ("}{voivodeship.shortageProfessions.length}{")"}</span>
                </div>
                <ProfessionGroup
                  professions={voivodeship.shortageProfessions}
                  variant="normal"
                  headingId="voivodeship-shortage"
                />
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-2">
          <button
            onClick={handleClose}
            className="px-3 py-1 bg-neutral-500 text-white text-sm rounded hover:bg-neutral-600 transition-colors"
          >
            {"Zamknij"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarometerModal;
